import React, { useState } from 'react';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Card, Button, Form, Input, Row, Col, Typography, Divider, message, Alert } from 'antd';
import { LoadingOutlined, WarningOutlined } from '@ant-design/icons';
import { trackBeginCheckout } from '../../../utils/analytics';
import stripePromise, { stripeConfig } from '../../../config/stripe';

const { Title, Text, Paragraph } = Typography;

// Stripe card element styling
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
      padding: '12px',
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

const CheckoutForm = ({ cartItems, onPaymentSuccess, onPaymentError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const handleSubmit = async (values) => {
    if (!stripe || !elements) {
      return;
    }

    // Track checkout initiation
    trackBeginCheckout(cartItems);
    
    setLoading(true);

    const card = elements.getElement(CardElement);

    try {
      // Create payment method
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: card,
        billing_details: {
          name: values.name,
          email: values.email,
          address: {
            line1: values.address,
            city: values.city,
            state: values.state,
            postal_code: values.zipCode,
            country: 'US',
          },
        },
      });

      if (error) {
        console.error('Error creating payment method:', error);
        message.error(error.message);
        setLoading(false);
        return;
      }

      // Create payment intent with backend
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:6543';
      
      console.log('Creating payment intent with backend:', {
        paymentMethodId: paymentMethod.id,
        amount: Math.round(getTotalPrice() * 100),
        customer: values
      });

      const response = await fetch(`${backendUrl}/api/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: Math.round(getTotalPrice() * 100), // Amount in cents
          currency: 'usd',
          paymentMethodId: paymentMethod.id,
          items: cartItems,
          customer: values
        })
      });

      const paymentResult = await response.json();

      if (paymentResult.error) {
        console.error('Payment failed:', paymentResult.error);
        message.error(paymentResult.error);
        onPaymentError && onPaymentError({ message: paymentResult.error });
        setLoading(false);
        return;
      }

      if (paymentResult.requires_action) {
        // Handle additional authentication (3D Secure, etc.)
        const { error: confirmError } = await stripe.confirmCardPayment(paymentResult.client_secret);
        
        if (confirmError) {
          console.error('Authentication failed:', confirmError);
          message.error(confirmError.message);
          onPaymentError && onPaymentError(confirmError);
          setLoading(false);
          return;
        }
      }

      // Payment successful
      console.log('Payment succeeded:', paymentResult.payment_intent);
      message.success('Payment Successful! 🎉');
      
      onPaymentSuccess && onPaymentSuccess({
        id: paymentResult.payment_intent?.id || `pi_${Date.now()}`,
        status: 'succeeded',
        amount: Math.round(getTotalPrice() * 100),
        currency: 'usd',
        customer: values,
        paymentMethod: paymentMethod,
        created: Date.now()
      });
    } catch (err) {
      console.error('Payment error:', err);
      message.error('Payment failed. Please try again.');
      onPaymentError && onPaymentError(err);
    }

    setLoading(false);
  };

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '20px' }}>
      <Title level={2} style={{ textAlign: 'center', marginBottom: '30px' }}>
        Complete Your Preorder
      </Title>
      <Paragraph style={{ textAlign: 'center', marginTop: '-20px', marginBottom: '30px' }}>
        Preordering guarantees your Lumi in summer 2027.
      </Paragraph>

      <Row gutter={[32, 32]}>
        {/* Order Summary */}
        <Col xs={24} lg={10}>
          <Card title="Preorder Summary" style={{ height: 'fit-content' }}>
            {cartItems.map(item => (
              <div key={item.id} style={{ marginBottom: '16px' }}>
                <Row justify="space-between">
                  <Col>
                    <Text strong>{item.name}</Text>
                    <div>
                      <Text type="secondary">Qty: {item.quantity} × ${item.price}</Text>
                    </div>
                  </Col>
                  <Col>
                    <Text strong>${(item.price * item.quantity).toLocaleString()}</Text>
                  </Col>
                </Row>
                <Divider style={{ margin: '12px 0' }} />
              </div>
            ))}
            <Row justify="space-between" style={{ marginTop: '20px' }}>
              <Col>
                <Title level={4}>Total:</Title>
              </Col>
              <Col>
                <Title level={4} style={{ color: '#e74c3c' }}>
                  ${getTotalPrice().toLocaleString()}
                </Title>
              </Col>
            </Row>
          </Card>
        </Col>

        {/* Payment Form */}
        <Col xs={24} lg={14}>
          <Card title="Payment & Shipping Information">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              size="large"
            >
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="name"
                    label="Full Name"
                    rules={[{ required: true, message: 'Please enter your full name' }]}
                  >
                    <Input placeholder="John Doe" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="email"
                    label="Email"
                    rules={[
                      { required: true, message: 'Please enter your email' },
                      { type: 'email', message: 'Please enter a valid email' }
                    ]}
                  >
                    <Input placeholder="john@example.com" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="address"
                label="Address"
                rules={[{ required: true, message: 'Please enter your address' }]}
              >
                <Input placeholder="123 Main St" />
              </Form.Item>

              <Row gutter={[16, 16]}>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="city"
                    label="City"
                    rules={[{ required: true, message: 'Please enter your city' }]}
                  >
                    <Input placeholder="New York" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="state"
                    label="State"
                    rules={[{ required: true, message: 'Please enter your state' }]}
                  >
                    <Input placeholder="NY" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="zipCode"
                    label="ZIP Code"
                    rules={[{ required: true, message: 'Please enter your ZIP code' }]}
                  >
                    <Input placeholder="10001" />
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Title level={4}>Payment Information</Title>
              
              <div style={{ 
                border: '1px solid #d9d9d9', 
                borderRadius: '6px', 
                padding: '12px 16px',
                marginBottom: '24px',
                backgroundColor: '#fafafa'
              }}>
                <CardElement options={cardElementOptions} />
              </div>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  block
                  loading={loading}
                  disabled={!stripe}
                  style={{
                    background: '#e74c3c',
                    borderColor: '#e74c3c',
                    height: '50px',
                    fontSize: '18px',
                    fontWeight: 'bold'
                  }}
                  icon={loading && <LoadingOutlined />}
                >
                  {loading ? 'Processing Payment...' : `Preorder for $${getTotalPrice().toLocaleString()}`}
                </Button>
              </Form.Item>

              <div style={{ textAlign: 'center', marginTop: '16px' }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  🔒 Your payment information is secure and encrypted
                </Text>
              </div>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

const StripeCheckout = ({ cartItems, onPaymentSuccess, onPaymentError }) => {
  // Check if Stripe is properly configured
  if (!stripeConfig.isValidKey) {
    return (
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '40px 20px' }}>
        <Alert
          message="Payment System Setup Required"
          description={
            <div>
              <p>To process payments, you need to configure your Stripe keys:</p>
              <ol>
                <li>Sign up at <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer">Stripe Dashboard</a></li>
                <li>Get your publishable key from the API Keys section</li>
                <li>Update your <code>.env.development</code> file:</li>
              </ol>
              <div style={{ 
                background: '#f6f8fa', 
                padding: '12px', 
                borderRadius: '4px', 
                fontFamily: 'monospace',
                margin: '16px 0' 
              }}>
                REACT_APP_STRIPE_PUBLISHABLE_KEY=pk_test_your_actual_key_here
              </div>
              <p><strong>For testing:</strong> Use test card 4242424242424242</p>
              <p style={{ marginTop: '20px' }}>
                <Button type="primary" onClick={() => window.location.reload()}>
                  Reload After Configuration
                </Button>
              </p>
            </div>
          }
          type="warning"
          icon={<WarningOutlined />}
          showIcon
        />
      </div>
    );
  }

  if (!stripePromise) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <Alert
          message="Unable to Load Payment System"
          description="Please check your Stripe configuration and try again."
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <CheckoutForm 
        cartItems={cartItems}
        onPaymentSuccess={onPaymentSuccess}
        onPaymentError={onPaymentError}
      />
    </Elements>
  );
};

export default StripeCheckout;